import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { WorkspaceContext } from '../context/WorkspaceContext';

// ---------------------------------------------------------------------------
// Types (mirror full-stack/src/conversation.rs)
// ---------------------------------------------------------------------------

export interface Conversation {
  id: string;
  workspace_id: string;
  user_id: string;
  title: string | null;
  model_id: string;
  status: ConversationStatus;
  created_at: number;
  pending_actions: PendingAction[] | null;
}

export type ConversationStatus =
  | { type: 'idle' }
  | { type: 'inferring' }
  | { type: 'awaiting_approval' };

export interface PendingAction {
  id: string;
  tool_use_id: string;
  tool_name: string;
  tool_input: unknown;
  is_write: boolean;
  status: ActionStatus;
  result: PendingActionResult | null;
}

export interface PendingActionResult {
  content: string;
  is_error: boolean;
}

export type ActionStatus =
  | { type: 'pending' }
  | { type: 'approved' }
  | { type: 'rejected'; reason?: string };

export interface ConversationMessage {
  id: string;
  prev_id: string | null;
  conversation_id: string;
  created_at: number;
  role: StoredMessageRole;
  content: StoredContentBlock[];
  bedrock_metadata: BedrockResponseMetadata | null;
  page_context: PageContext | null;
}

export type StoredMessageRole =
  | { type: 'user' }
  | { type: 'assistant' }
  | { type: 'pending_actions' }
  | { type: 'summary' };

export interface ImageAssetBlockFields {
  s3_key: string;
  format: 'png' | 'jpeg' | 'gif' | 'webp';
  sha256: string;
  original_filename?: string | null;
  byte_size?: number | null;
}

export type StoredContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; tool_use_id: string; name: string; input: unknown; requires_approval?: boolean }
  | { type: 'tool_result'; tool_use_id: string; content: string; is_error: boolean }
  | {
      type: 'error';
      message: string;
      correlation_id: string;
      error_code?: string | null;
      retry_after_secs?: number | null;
    }
  | ({ type: 'image' } & ImageAssetBlockFields)
  | {
      type: 'tool_result_image';
      tool_use_id: string;
      asset: ImageAssetBlockFields;
      is_error?: boolean;
    };

export interface BedrockResponseMetadata {
  model_id: string;
  input_tokens: number;
  output_tokens: number;
  stop_reason: string;
}

export interface ContextInfo {
  estimated_tokens_used: number;
  context_window: number;
  usage_ratio: number;
}

export interface PageContext {
  entity_type?: string;
  entity_id?: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  url: string,
  token: string,
  options?: RequestInit,
): Promise<T> {
  const resp = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text}`);
  }
  // 202 and 204 may have no body
  if (resp.status === 204 || resp.status === 202) {
    const text = await resp.text().catch(() => '');
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }
  return resp.json();
}

// ---------------------------------------------------------------------------
// Hook: manage a single conversation
// ---------------------------------------------------------------------------

export function useConversation(workspaceId: string) {
  const { token } = useAuth();
  const apiUrl = process.env.API_URL ?? '';
  const { subscribeConversation } = useContext(WorkspaceContext);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextInfo, setContextInfo] = useState<ContextInfo | null>(null);
  const [compacting, setCompacting] = useState(false);

  const activeConvIdRef = useRef<string | null>(null);
  // True after sending a message, until we see a new assistant message
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  // The ID of the latest assistant message at the time sendMessage was called.
  // We clear `waitingForResponse` only when we see a newer assistant message.
  const lastAssistantIdAtSendRef = useRef<string | null>(null);
  // Safety timeout: if no response arrives within this window, stop showing thinking.
  const waitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep ref in sync
  useEffect(() => {
    activeConvIdRef.current = activeConversation?.id ?? null;
  }, [activeConversation?.id]);

  // ── List conversations ──────────────────────────────────────────
  const listConversations = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch<{ conversations: Conversation[] }>(
        `${apiUrl}/api/conversations`,
        token,
      );
      setConversations(data.conversations);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setConversationsLoaded(true);
    }
  }, [token, apiUrl]);

  // ── Create conversation ─────────────────────────────────────────
  const createConversation = useCallback(
    async (title?: string, modelId?: string) => {
      if (!token) return null;
      setError(null);
      try {
        const data = await apiFetch<{ conversation: Conversation }>(
          `${apiUrl}/api/conversations`,
          token,
          {
            method: 'POST',
            body: JSON.stringify({
              workspace_id: workspaceId,
              model_id: modelId ?? null,
              title: title ?? null,
              page_context: null,
            }),
          },
        );
        setActiveConversation(data.conversation);
        setMessages([]);
        await listConversations();
        return data.conversation;
      } catch (e: any) {
        setError(e.message);
        return null;
      }
    },
    [token, apiUrl, workspaceId, listConversations],
  );

  // ── Select conversation ─────────────────────────────────────────
  const selectConversation = useCallback(
    async (conv: Conversation) => {
      setActiveConversation(conv);
      setMessages([]);
      if (!token) return;
      setLoading(true);
      try {
        const data = await apiFetch<{ messages: ConversationMessage[] }>(
          `${apiUrl}/api/conversations/${conv.id}/messages?limit=100`,
          token,
        );
        setMessages(data.messages);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [token, apiUrl],
  );

  // ── Refresh messages ────────────────────────────────────────────
  // Uses ref for conv ID so this callback is stable across conversation changes.
  const refreshMessages = useCallback(async () => {
    const convId = activeConvIdRef.current;
    if (!token || !convId) return;
    try {
      const data = await apiFetch<{ messages: ConversationMessage[] }>(
        `${apiUrl}/api/conversations/${convId}/messages?limit=100`,
        token,
      );
      setMessages(data.messages);
      const convData = await apiFetch<{ conversations: Conversation[] }>(
        `${apiUrl}/api/conversations`,
        token,
      );
      const updated = convData.conversations.find(c => c.id === convId);
      if (updated) setActiveConversation(updated);
    } catch {
      // Silently fail on refresh — don't overwrite user-facing errors
    }
  }, [token, apiUrl]);

  // ── Send message ────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (
      text: string,
      pageContext?: PageContext,
      attachments?: ImageAssetBlockFields[],
    ) => {
      if (!token || !activeConversation) return;

      // Record the latest assistant message ID BEFORE sending, using functional
      // setState to read the current state (avoids stale closure).
      setMessages((current) => {
        const latestAssistant = [...current].reverse().find((m) => m.role.type === 'assistant');
        lastAssistantIdAtSendRef.current = latestAssistant?.id ?? null;
        return current;
      });

      // Clear any previous waiting timeout, arm a new one.
      if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = setTimeout(() => {
        setWaitingForResponse(false);
      }, 60_000);

      // Eagerly show the thinking indicator — don't wait for the POST to return
      // or for the websocket to tell us status=inferring.
      setWaitingForResponse(true);
      setSending(true);
      setError(null);
      try {
        await apiFetch<{ message_id: string }>(
          `${apiUrl}/api/conversations/${activeConversation.id}/messages`,
          token,
          {
            method: 'POST',
            body: JSON.stringify({
              content: text,
              page_context: pageContext ?? null,
              attachments: attachments ?? [],
            }),
          },
        );
        // Fetch the user message we just created; the WebSocket will deliver the response.
        await refreshMessages();
      } catch (e: any) {
        setError(e.message);
        setWaitingForResponse(false);
        if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current);
      } finally {
        setSending(false);
      }
    },
    [token, apiUrl, activeConversation, refreshMessages],
  );

  // ── Approve / Reject tool calls ──────────────────────────────────
  const approveToolCall = useCallback(
    async (toolUseId: string) => {
      if (!token || !activeConversation) return;
      try {
        await apiFetch<void>(
          `${apiUrl}/api/conversations/${activeConversation.id}/tool-calls/${toolUseId}/approve`,
          token,
          { method: 'POST' },
        );
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, apiUrl, activeConversation],
  );

  const rejectToolCall = useCallback(
    async (toolUseId: string, reason?: string) => {
      if (!token || !activeConversation) return;
      try {
        await apiFetch<void>(
          `${apiUrl}/api/conversations/${activeConversation.id}/tool-calls/${toolUseId}/reject`,
          token,
          { method: 'POST', body: JSON.stringify({ reason: reason ?? null }) },
        );
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, apiUrl, activeConversation],
  );

  // ── Delete conversation ──────────────────────────────────────────
  const deleteConversation = useCallback(
    async (convId: string) => {
      if (!token) return;
      setError(null);
      try {
        await apiFetch<void>(`${apiUrl}/api/conversations/${convId}`, token, {
          method: 'DELETE',
        });
        if (activeConversation?.id === convId) {
          setActiveConversation(null);
          setMessages([]);
        }
        await listConversations();
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, apiUrl, activeConversation, listConversations],
  );

  // ── Context info ─────────────────────────────────────────────────
  const fetchContextInfo = useCallback(async () => {
    const convId = activeConvIdRef.current;
    if (!token || !convId) return;
    try {
      const data = await apiFetch<ContextInfo>(
        `${apiUrl}/api/conversations/${convId}/context`,
        token,
      );
      setContextInfo(data);
    } catch {
      // Non-critical — don't overwrite error state
    }
  }, [token, apiUrl]);

  // ── Compact conversation ────────────────────────────────────────
  const compactConversation = useCallback(async () => {
    const convId = activeConvIdRef.current;
    if (!token || !convId) return;
    setCompacting(true);
    setError(null);
    try {
      await apiFetch<{ summary_message_id: string }>(
        `${apiUrl}/api/conversations/${convId}/compact`,
        token,
        { method: 'POST' },
      );
      await refreshMessages();
      await fetchContextInfo();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCompacting(false);
    }
  }, [token, apiUrl, refreshMessages, fetchContextInfo]);

  // Refresh context info when conversation changes or messages update
  useEffect(() => {
    fetchContextInfo();
  }, [activeConversation?.id, messages.length, fetchContextInfo]);

  // ── WebSocket: receive messages and status changes in real time ──
  useEffect(() => {
    const convId = activeConversation?.id;
    if (!convId || !subscribeConversation) return;

    const unsubscribe = subscribeConversation(
      convId,
      // onMessage: append new message to state
      (rawMessage: unknown) => {
        const msg = rawMessage as ConversationMessage;
        setMessages((prev) => {
          const existingIdx = prev.findIndex((m) => m.id === msg.id);
          if (existingIdx >= 0) {
            // Message updated (e.g. tool_result appended) — replace in place
            const updated = [...prev];
            updated[existingIdx] = msg;
            return updated;
          }
          return [...prev, msg];
        });
      },
      // onStatus: update conversation status
      (_conversationId: string, status: { type: string }) => {
        setActiveConversation((prev) => {
          if (!prev) return prev;
          return { ...prev, status: status as ConversationStatus };
        });

        // When status becomes awaiting_approval, fetch the full conversation
        // to get pending_actions (not sent over the WebSocket).
        if (status.type === 'awaiting_approval' && token) {
          apiFetch<{ conversations: Conversation[] }>(
            `${apiUrl}/api/conversations`,
            token,
          ).then((data) => {
            const updated = data.conversations.find((c) => c.id === _conversationId);
            if (updated) setActiveConversation(updated);
          }).catch(() => {});
        }
      },
    );

    return unsubscribe;
  }, [activeConversation?.id, subscribeConversation]);

  // Stop waiting once we see a NEW assistant message (different from the last one at send time)
  // AND the conversation is no longer inferring.
  useEffect(() => {
    if (!waitingForResponse) return;
    const latestAssistant = [...messages].reverse().find((m) => m.role.type === 'assistant');
    const latestAssistantId = latestAssistant?.id ?? null;
    const hasNewAssistant =
      latestAssistantId !== null && latestAssistantId !== lastAssistantIdAtSendRef.current;
    const isStillInferring = activeConversation?.status.type === 'inferring';
    if (hasNewAssistant && !isStillInferring) {
      setWaitingForResponse(false);
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    }
  }, [waitingForResponse, messages, activeConversation?.status.type]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current);
    };
  }, []);

  return {
    conversations,
    conversationsLoaded,
    activeConversation,
    messages,
    loading,
    sending,
    error,
    listConversations,
    createConversation,
    selectConversation,
    sendMessage,
    refreshMessages,
    approveToolCall,
    rejectToolCall,
    deleteConversation,
    waitingForResponse,
    contextInfo,
    compacting,
    compactConversation,
  };
}
